import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { keys } from '../api/keys'

/**
 * Whether the screen is still being kept current.
 *
 * `stalled` is its own state and not a variant of `offline` on purpose: the
 * server said, in as many words, that it can no longer see changes. A panel that
 * has quietly stopped updating is a panel somebody will trust, and trusting a
 * frozen inbox means a client waits.
 */
export type LiveStatus = 'connecting' | 'live' | 'stalled' | 'offline'

/**
 * Keep the open screens current.
 *
 * The stream is treated as a doorbell, never as the truth: an event says
 * something about a conversation changed, and the panel goes and reads it. The
 * payload carries a status and a version, but acting on those directly would put
 * two copies of the state on screen — one from the last fetch and one from the
 * stream — and the approve button would end up guarding the wrong one.
 */
export function useLiveUpdates(threadId: string | null): LiveStatus {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<LiveStatus>('connecting')

  useEffect(() => {
    const url = threadId === null ? '/api/events' : `/api/events?thread=${encodeURIComponent(threadId)}`
    const source = new EventSource(url)

    const refreshList = () => {
      void queryClient.invalidateQueries({ queryKey: keys.anyThreadList() })
    }

    // Reconnecting is not the same as connecting, and the difference is the
    // worst failure this panel can have.
    //
    // Each connection gets a fresh watcher on the server whose first poll is
    // deliberately swallowed as a baseline, so a reconnected stream reports only
    // what changes after it reconnects. Everything that happened during the gap
    // — a client writing, a draft being composed — is simply absent, the badge
    // goes green, and the screen looks healthy while it is stale. Nothing else
    // fills the hole: window-focus refetching is off and neither query polls.
    //
    // So the first connection sets the status and every later one refetches.
    let connected = false
    const cameBack = () => {
      setStatus('live')
      if (!connected) {
        connected = true
        return
      }
      refreshList()
      if (threadId !== null) {
        void queryClient.invalidateQueries({ queryKey: keys.thread(threadId) })
      }
    }

    source.addEventListener('open', cameBack)
    source.addEventListener('hello', cameBack)
    source.addEventListener('resumed', cameBack)
    source.addEventListener('stalled', () => setStatus('stalled'))

    source.addEventListener('thread', (event) => {
      setStatus('live')
      refreshList()
      // A conversation changing while it is open matters more than the list
      // behind it: the mode may have moved to human_paused, or a new client
      // message may have made the draft on screen stale.
      const changed = idOf(event)
      if (threadId !== null && changed === threadId) {
        void queryClient.invalidateQueries({ queryKey: keys.thread(threadId) })
      }
    })

    source.addEventListener('draft', () => {
      setStatus('live')
      if (threadId === null) return
      // keys.messages() is a child of keys.thread(), so this refetches both: a
      // draft appearing usually means a message arrived to prompt it.
      void queryClient.invalidateQueries({ queryKey: keys.thread(threadId) })
      refreshList()
    })

    source.addEventListener('ping', () => setStatus('live'))

    source.addEventListener('error', () => {
      // EventSource reconnects on its own, so this is a report rather than a
      // repair. Saying "offline" while it retries is honest: until it is back,
      // what is on screen is as old as the last fetch.
      setStatus('offline')
    })

    return () => {
      source.close()
    }
  }, [queryClient, threadId])

  return status
}

function idOf(event: Event): string | null {
  if (!(event instanceof MessageEvent)) return null
  try {
    const payload: unknown = JSON.parse(String(event.data))
    if (typeof payload !== 'object' || payload === null) return null
    const id = (payload as Record<string, unknown>)['id']
    return typeof id === 'string' ? id : null
  } catch {
    // A frame we cannot read is not a reason to tear the stream down; the next
    // one is two seconds away.
    return null
  }
}
